import React from "react";
import MeshField from "./MeshField";
import Reveal from "./Reveal";
import Doodle from "./Doodle";
import Highlighter from "./Highlighter";
import FounderFilmPlayer from "./FounderFilmPlayer";

// The founder film.
//
// HOSTING: a plain static MP4 on our OWN origin, imported so Vite fingerprints
// it and serves it from the same CDN as the rest of the build. No YouTube, no
// Vimeo, no iframe, no third-party player script. That is the security answer
// as well as the tasteful one: a video file is inert bytes served with
// Content-Type video/mp4, there is no execution path through it, it reaches no
// server of ours (Vercel's CDN answers, the VPS is never involved), and because
// nothing is embedded there is no external origin that could run script in the
// page or read anything from it. An embed would have been the opposite trade:
// someone else's JavaScript, running with our page's privileges, plus their
// cookies on our visitors. Measured teardown of plysocial.com (2026-08-01)
// found they self-host exactly this way: /videos/*.mp4, no iframes at all.
//
// PLAYBACK: nothing plays until the section is actually on screen. It is muted,
// inline, and loops, so arriving at the site is silent. The founder asked for
// this specifically, and it is also what browsers require: an unmuted autoplay
// is refused, and a video that starts on load burns a visitor's data on
// something they may never scroll to.
//
// SOUND: the captions are burned into the picture, so muted playback carries
// the whole message and the unmute button is a genuine extra rather than a
// requirement. That is why there is no separate caption track.
//
// preload="metadata" fetches a few KB (dimensions and duration) rather than the
// file. The bytes are only pulled when the section is approached.
const FounderFilm: React.FC = () => {
  // Playback, sound and reduced-motion live in FounderFilmPlayer, shared
  // with /about so there is one implementation of those rules, not two.
  return (
    <section className="relative w-full bg-cream py-24 md:py-32 px-6 overflow-hidden">
      <MeshField variant="ambient" />

      <Doodle
        name="squiggle"
        size={64}
        rotate={-8}
        className="absolute w-7 h-7 md:w-auto md:h-auto left-[6%] top-[14%] text-teal/30 story-float"
      />
      <Doodle
        name="starFilled"
        size={20}
        className="absolute w-3 h-3 md:w-auto md:h-auto right-[9%] top-[18%] text-salmon/45"
      />

      <div className="relative max-w-6xl mx-auto grid md:grid-cols-2 gap-12 md:gap-16 items-center">
        <Reveal y={26}>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-salmon-dark mb-5">
            The people behind it
          </p>
          <h2
            className="font-headline text-ink tracking-[-0.025em] leading-[1.16] text-[clamp(2rem,5.8vw,3.6rem)] text-balance"
            style={{ fontWeight: 700 }}
          >
            Meet <Highlighter tone="coral">Josh &amp; Adrien.</Highlighter>
          </h2>
          <p className="mt-5 text-lg text-ink/70 leading-relaxed max-w-md">
            Two friends who found value in the small things: a coffee after
            work, a Sunday pub quiz, a walk that turns into dinner. They built
            LetsLoop so those are easier for everyone else to find.
          </p>
          {/* /70 not /50: at /50 this small print measured 2.75:1, which is the
              site's worst-case eyebrow contrast rather than body-copy contrast. */}
          <p className="mt-4 text-sm text-ink/70">
            Fifty seconds, filmed around London. It plays when you reach it,
            never before, and the sound stays off unless you ask for it.
          </p>
        </Reveal>

        <Reveal y={30} delay={120} className="relative">
          <FounderFilmPlayer />
        </Reveal>
      </div>
    </section>
  );
};

export default FounderFilm;
